import React, { useState } from 'react';
import {
  useCpcCalculations,
  useCpcRoleRates,
  useCpcActiveFactorSet,
  useCalculateAssessment,
  useDeleteCalculation,
  useSetActualPt,
} from '../../ctb/cpc-hooks';
import type {
  Framework,
  DsgvoDepth,
  DocState,
  AssessmentDepth,
  AssessmentUserInput,
  CpcCalculationListItem,
  CpcRoleRate,
  AssessmentResult,
  CpcRoleDefinition,
} from '../../ctb/cpc-types';
import { CpcClockodoPanel } from '../../ctb/CpcClockodoPanel';
import { Modal } from '../../components/Modal';
import styles from './CpcTab.module.css';

// ── Label Maps ────────────────────────────────────────────────────────────────

const FRAMEWORK_LABELS: Record<Framework, string> = {
  iso27001: 'ISO 27001',
  marisk: 'MaRisk',
  dora: 'DORA',
  ebaOutsourcing: 'EBA Outsourcing',
  nis2: 'NIS-2',
  cra: 'CRA (Cyber Resilience Act)',
  iso22301: 'ISO 22301',
};

const ALL_FRAMEWORKS: Framework[] = [
  'iso27001',
  'marisk',
  'dora',
  'ebaOutsourcing',
  'nis2',
  'cra',
  'iso22301',
];

const PHASE_LABELS: Record<string, string> = {
  kickoff: 'Kickoff & Scoping',
  docAnalysis: 'Dokumentenanalyse',
  interviews: 'Interviews / Workshops',
  gapAnalysis: 'Gap-Analyse',
  thirdPartyReview: 'Drittparteien-Review',
  targetAndRoadmap: 'Zielbild & Roadmap',
  reporting: 'Reporting & Präsentation',
};

function getRoleLabel(key: string, roles: CpcRoleDefinition[]): string {
  return roles.find((r) => r.key === key)?.label ?? key;
}

const FACTOR_LABELS: Record<string, string> = {
  sizeClass: 'Größenklasse',
  industrySectorFactor: 'Branchenfaktor',
  locationFactor: 'Standortfaktor',
  criticalFunctionsSurcharge: 'Zuschlag kritische Funktionen',
  thirdPartySurcharge: 'Drittparteien-Zuschlag',
  maturityDiscount: 'Reifegrad-Rabatt',
  docFactor: 'Dokumentationsfaktor',
  depthFactor: 'Tiefenfaktor',
  dsgvoFactor: 'DSGVO-Faktor',
  assumptionRiskFactor: 'Annahme-Risikofaktor',
};

// ── Helper ────────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatPt(pt: number | undefined | null): string {
  if (pt == null || isNaN(pt as number)) return '0';
  return pt % 1 === 0 ? String(pt) : pt.toFixed(1);
}

// ── Types ─────────────────────────────────────────────────────────────────────

type MainView = 'history' | 'wizard' | 'result';
type ResultTab = 'phases' | 'roles' | 'factors' | 'assumptions';

interface WizardState {
  step: 1 | 2 | 3 | 4;
  revisionLabel: string;
  frameworkScope: Framework[];
  baitIncluded: boolean;
  dsgvoDepth: DsgvoDepth;
  docState: DocState;
  depth: AssessmentDepth;
  addOns: { executiveTraining: boolean; auditReadinessCheck: boolean };
  notes: string;
}

const DEFAULT_WIZARD: WizardState = {
  step: 1,
  revisionLabel: '',
  frameworkScope: [],
  baitIncluded: false,
  dsgvoDepth: 'standard',
  docState: 'partial',
  depth: 'standardCheck',
  addOns: { executiveTraining: false, auditReadinessCheck: false },
  notes: '',
};

// ── Props ─────────────────────────────────────────────────────────────────────

interface CpcTabProps {
  companyId: string;
  clockodoCustomerId?: number | null;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function CpcTab({ companyId, clockodoCustomerId }: CpcTabProps) {
  const [view, setView] = useState<MainView>('history');
  const [wizard, setWizard] = useState<WizardState>(DEFAULT_WIZARD);
  const [selectedCalc, setSelectedCalc] = useState<CpcCalculationListItem | null>(null);
  const [resultTab, setResultTab] = useState<ResultTab>('phases');
  const [isActualPtOpen, setActualPtOpen] = useState(false);
  const [actualPtForm, setActualPtForm] = useState<Record<string, string>>({ totalPT: '' });

  const { data: calcsData, isLoading, error } = useCpcCalculations(companyId);
  const { data: roleRatesData } = useCpcRoleRates();
  const { data: activeFs } = useCpcActiveFactorSet();
  const activeRoles: CpcRoleDefinition[] = activeFs?.payload.roles ?? [];
  const calculateMutation = useCalculateAssessment(companyId);
  const deleteCalcMutation = useDeleteCalculation(companyId);
  const setActualPtMutation = useSetActualPt(selectedCalc?.id ?? '', companyId);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const calcErrorMsg = (() => {
    if (!calculateMutation.error) return undefined;
    const data = (calculateMutation.error as any)?.response?.data;
    if (!data) return calculateMutation.error.message;
    const msg = data.message;
    if (Array.isArray(msg)) return msg.join(' | ');
    return msg || calculateMutation.error.message;
  })();

  const calculations = calcsData ?? [];
  const roleRates = roleRatesData ?? [];

  // ── Wizard helpers ───────────────────────────────────────────────────────

  function toggleFramework(fw: Framework) {
    setWizard((prev) => ({
      ...prev,
      frameworkScope: prev.frameworkScope.includes(fw)
        ? prev.frameworkScope.filter((f) => f !== fw)
        : [...prev.frameworkScope, fw],
    }));
  }

  function canAdvanceStep(): boolean {
    if (wizard.step === 1) {
      return wizard.frameworkScope.length > 0 && wizard.revisionLabel.trim().length > 0;
    }
    return true;
  }

  function handleDelete(id: string) {
    setDeletingId(id);
    deleteCalcMutation.mutate(id, { onSettled: () => setDeletingId(null) });
  }

  function handleSubmitWizard() {
    const input: AssessmentUserInput = {
      frameworkScope: wizard.frameworkScope,
      baitIncluded: wizard.baitIncluded,
      dsgvoDepth: wizard.dsgvoDepth,
      docState: wizard.docState,
      depth: wizard.depth,
      addOns: wizard.addOns,
    };
    calculateMutation.mutate(
      { revisionLabel: wizard.revisionLabel.trim(), input, notes: wizard.notes || undefined },
      {
        onSuccess: (res) => {
          // Build a pseudo-calc from the response to show result
          const pseudo: CpcCalculationListItem = {
            id: res.calculationId,
            revisionLabel: wizard.revisionLabel,
            createdAt: new Date().toISOString(),
            createdBy: '',
            product: 'CPC',
            actualPt: null,
            result: res.result,
            factorSet: { version: res.factorSetVersion },
            clockodoLinkStatus: 'not_linked',
          };
          setSelectedCalc(pseudo);
          setResultTab('phases');
          setView('result');
          setWizard(DEFAULT_WIZARD);
        },
      },
    );
  }

  // ── Actual PT submit ─────────────────────────────────────────────────────

  function openActualPtModal() {
    const initial: Record<string, string> = { totalPT: '' };
    if (selectedCalc) {
      Object.keys(selectedCalc.result.roleBreakdown).forEach((k) => { initial[k] = ''; });
    }
    setActualPtForm(initial);
    setActualPtOpen(true);
  }

  function handleSetActualPt() {
    const roleBreakdown: Record<string, number> = {};
    Object.entries(actualPtForm).forEach(([k, v]) => {
      if (k !== 'totalPT') roleBreakdown[k] = parseFloat(v) || 0;
    });
    setActualPtMutation.mutate(
      {
        actualPt: {
          totalPT: parseFloat(actualPtForm.totalPT) || 0,
          roleBreakdown,
        },
      },
      {
        onSuccess: () => {
          setActualPtOpen(false);
          setActualPtForm({ totalPT: '' });
        },
      },
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (isLoading) return <p className={styles.loading}>Lade Kalkulationen...</p>;
  if (error) return <p className={styles.error}>Kalkulationen konnten nicht geladen werden.</p>;

  if (view === 'wizard') {
    return (
      <WizardView
        wizard={wizard}
        setWizard={setWizard}
        toggleFramework={toggleFramework}
        canAdvanceStep={canAdvanceStep}
        onSubmit={handleSubmitWizard}
        isPending={calculateMutation.isPending}
        error={calcErrorMsg}
        onCancel={() => { setWizard(DEFAULT_WIZARD); setView('history'); }}
      />
    );
  }

  if (view === 'result' && selectedCalc) {
    return (
      <>
        <ResultView
          calc={selectedCalc}
          roleRates={roleRates}
          roles={activeRoles}
          resultTab={resultTab}
          setResultTab={setResultTab}
          onNewCalc={() => { setView('wizard'); setSelectedCalc(null); }}
          onSetActualPt={openActualPtModal}
        />
        <Modal
          isOpen={isActualPtOpen}
          onClose={() => setActualPtOpen(false)}
          title="Ist-PT eintragen"
          maxWidth="480px"
        >
          <ActualPtForm
            form={actualPtForm}
            setForm={setActualPtForm}
            roles={selectedCalc ? Object.keys(selectedCalc.result.roleBreakdown).map((key) => ({
              key,
              label: getRoleLabel(key, activeRoles),
            })) : activeRoles}
            onSubmit={handleSetActualPt}
            onCancel={() => setActualPtOpen(false)}
            isPending={setActualPtMutation.isPending}
          />
        </Modal>
      </>
    );
  }

  // History view
  return (
    <HistoryView
      calculations={calculations}
      companyId={companyId}
      clockodoCustomerId={clockodoCustomerId}
      onNew={() => setView('wizard')}
      onSelect={(calc) => { setSelectedCalc(calc); setResultTab('phases'); setView('result'); }}
      onDelete={handleDelete}
      deletingId={deletingId}
    />
  );
}

// ── History View ──────────────────────────────────────────────────────────────

const LINK_STATUS_LABELS: Record<string, string> = {
  not_linked: '—',
  linked: '✓ Verknüpft',
  sync_error: '⚠ Fehler',
};
const LINK_STATUS_COLORS: Record<string, string> = {
  not_linked: 'var(--color-text-muted)',
  linked: 'var(--color-success, #198754)',
  sync_error: 'var(--color-danger, #dc3545)',
};

function HistoryView({
  calculations,
  companyId,
  clockodoCustomerId,
  onNew,
  onSelect,
  onDelete,
  deletingId,
}: {
  calculations: CpcCalculationListItem[];
  companyId: string;
  clockodoCustomerId?: number | null;
  onNew: () => void;
  onSelect: (calc: CpcCalculationListItem) => void;
  onDelete: (id: string) => void;
  deletingId: string | null;
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [expandedClockodoId, setExpandedClockodoId] = useState<string | null>(null);

  function handleDeleteClick(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    setConfirmId(id);
  }

  function handleConfirm(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    setConfirmId(null);
    onDelete(id);
  }

  function handleCancel(e: React.MouseEvent) {
    e.stopPropagation();
    setConfirmId(null);
  }

  function toggleClockodo(e: React.MouseEvent, id: string) {
    e.stopPropagation();
    setExpandedClockodoId((prev) => (prev === id ? null : id));
  }

  return (
    <div className={styles.root}>
      <div className={styles.historyHeader}>
        <h2 className={styles.historyTitle}>Assessment-Kalkulationen</h2>
        <button className={styles.primaryBtn} onClick={onNew}>
          + Neue Assessment-Kalkulation
        </button>
      </div>

      {calculations.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-3" />
              <path d="M9 15h3l8.5-8.5a1.5 1.5 0 00-3-3L9 12v3" />
            </svg>
          </div>
          <p className={styles.emptyTitle}>Noch keine Kalkulation vorhanden</p>
          <p className={styles.emptySubtitle}>Erstelle die erste Assessment-Kalkulation für dieses Unternehmen.</p>
          <button className={styles.primaryBtn} onClick={onNew}>
            Erste Kalkulation erstellen
          </button>
        </div>
      ) : (
        <div className={styles.historyCard}>
          <table className={styles.historyTable}>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Revisions-Label</th>
                <th>PT erwartet</th>
                <th>Clockodo</th>
                <th>Faktor-Version</th>
                <th>Ersteller</th>
                <th>Ist-PT</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {calculations.map((calc) => (
                <React.Fragment key={calc.id}>
                  <tr onClick={() => onSelect(calc)} className={styles.historyRow}>
                    <td>{formatDate(calc.createdAt)}</td>
                    <td className={styles.revLabel}>{calc.revisionLabel}</td>
                    <td>
                      <span className={styles.ptBadge}>
                        {formatPt(calc.result.totalPT?.expected)} PT
                      </span>
                    </td>
                    <td onClick={(e) => toggleClockodo(e, calc.id)} style={{ cursor: 'pointer' }}>
                      <span style={{ fontSize: '0.78rem', color: LINK_STATUS_COLORS[calc.clockodoLinkStatus ?? 'not_linked'] }}>
                        {LINK_STATUS_LABELS[calc.clockodoLinkStatus ?? 'not_linked']}
                      </span>
                      {calc.actualPtCached && (
                        <span style={{ marginLeft: 6, fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                          ({Number(calc.actualPtCached).toFixed(1)} PT)
                        </span>
                      )}
                    </td>
                    <td className={styles.versionCell}>{calc.factorSet.version}</td>
                    <td className={styles.creatorCell}>{calc.createdBy || '—'}</td>
                    <td>
                      {calc.actualPt ? (
                        <span className={styles.actualPtBadge}>
                          {formatPt(calc.actualPt.totalPT)} PT
                        </span>
                      ) : (
                        <span className={styles.noActualPt}>—</span>
                      )}
                    </td>
                    <td className={styles.deleteCell} onClick={(e) => e.stopPropagation()}>
                      {confirmId === calc.id ? (
                        <span className={styles.deleteConfirm}>
                          Löschen?
                          <button className={styles.deleteConfirmYes} onClick={(e) => handleConfirm(e, calc.id)} disabled={deletingId === calc.id}>Ja</button>
                          <button className={styles.deleteConfirmNo} onClick={handleCancel}>Nein</button>
                        </span>
                      ) : (
                        <button
                          className={styles.deleteBtn}
                          onClick={(e) => handleDeleteClick(e, calc.id)}
                          disabled={deletingId === calc.id}
                          title="Kalkulation löschen"
                        >
                          {deletingId === calc.id ? '…' : '✕'}
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedClockodoId === calc.id && (
                    <tr>
                      <td colSpan={8} style={{ padding: '0 0.75rem 0.75rem', background: 'var(--color-bg)' }}>
                        <CpcClockodoPanel calculation={calc} companyId={companyId} clockodoCustomerId={clockodoCustomerId} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Wizard View ───────────────────────────────────────────────────────────────

function WizardView({
  wizard,
  setWizard,
  toggleFramework,
  canAdvanceStep,
  onSubmit,
  isPending,
  error,
  onCancel,
}: {
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState>>;
  toggleFramework: (fw: Framework) => void;
  canAdvanceStep: () => boolean;
  onSubmit: () => void;
  isPending: boolean;
  error?: string;
  onCancel: () => void;
}) {
  const steps = ['Framework-Scope', 'DSGVO-Tiefe', 'Dokumentation & Tiefe', 'Add-ons'];

  return (
    <div className={styles.root}>
      {/* Progress */}
      <div className={styles.wizardProgress}>
        {steps.map((label, i) => {
          const stepNum = (i + 1) as 1 | 2 | 3 | 4;
          const isActive = wizard.step === stepNum;
          const isDone = wizard.step > stepNum;
          return (
            <div key={label} className={`${styles.wizardStep} ${isActive ? styles.wizardStepActive : ''} ${isDone ? styles.wizardStepDone : ''}`}>
              <div className={styles.wizardStepNum}>{isDone ? '✓' : stepNum}</div>
              <span className={styles.wizardStepLabel}>{label}</span>
              {i < steps.length - 1 && <div className={styles.wizardStepLine} />}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <div className={styles.wizardCard}>
        {wizard.step === 1 && (
          <Step1
            wizard={wizard}
            setWizard={setWizard}
            toggleFramework={toggleFramework}
          />
        )}
        {wizard.step === 2 && (
          <Step2 wizard={wizard} setWizard={setWizard} />
        )}
        {wizard.step === 3 && (
          <Step3 wizard={wizard} setWizard={setWizard} />
        )}
        {wizard.step === 4 && (
          <Step4 wizard={wizard} setWizard={setWizard} />
        )}
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {/* Navigation */}
      <div className={styles.wizardNav}>
        <button className={styles.secondaryBtn} onClick={onCancel} disabled={isPending}>
          Abbrechen
        </button>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {wizard.step > 1 && (
            <button
              className={styles.secondaryBtn}
              onClick={() => setWizard((p) => ({ ...p, step: (p.step - 1) as 1 | 2 | 3 | 4 }))}
              disabled={isPending}
            >
              Zurück
            </button>
          )}
          {wizard.step < 4 ? (
            <button
              className={styles.primaryBtn}
              onClick={() => setWizard((p) => ({ ...p, step: (p.step + 1) as 1 | 2 | 3 | 4 }))}
              disabled={!canAdvanceStep()}
            >
              Weiter
            </button>
          ) : (
            <button
              className={styles.primaryBtn}
              onClick={onSubmit}
              disabled={isPending}
            >
              {isPending ? 'Berechne...' : 'Kalkulation starten'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Step1({
  wizard,
  setWizard,
  toggleFramework,
}: {
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState>>;
  toggleFramework: (fw: Framework) => void;
}) {
  return (
    <div className={styles.stepContent}>
      <h3 className={styles.stepTitle}>Framework-Scope & Revisions-Label</h3>
      <p className={styles.stepHint}>Wähle alle relevanten regulatorischen Frameworks für dieses Assessment.</p>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Revisions-Label *</label>
        <input
          className={styles.textInput}
          type="text"
          placeholder="z. B. Initial-Assessment 2025-Q1"
          value={wizard.revisionLabel}
          onChange={(e) => setWizard((p) => ({ ...p, revisionLabel: e.target.value }))}
          maxLength={80}
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Framework-Scope *</label>
        <div className={styles.checkboxGrid}>
          {ALL_FRAMEWORKS.map((fw) => (
            <label key={fw} className={styles.checkboxItem}>
              <input
                type="checkbox"
                checked={wizard.frameworkScope.includes(fw)}
                onChange={() => toggleFramework(fw)}
              />
              {FRAMEWORK_LABELS[fw]}
            </label>
          ))}
        </div>
      </div>

      {wizard.frameworkScope.includes('marisk') && (
        <div className={styles.formGroup}>
          <label className={styles.checkboxItem}>
            <input
              type="checkbox"
              checked={wizard.baitIncluded}
              onChange={(e) => setWizard((p) => ({ ...p, baitIncluded: e.target.checked }))}
            />
            <strong>BAIT eingeschlossen</strong> (Bankaufsichtliche Anforderungen an die IT)
          </label>
        </div>
      )}

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Notizen (optional)</label>
        <textarea
          className={styles.textarea}
          rows={3}
          placeholder="Zusätzliche Hinweise zur Kalkulation..."
          value={wizard.notes}
          onChange={(e) => setWizard((p) => ({ ...p, notes: e.target.value }))}
        />
      </div>
    </div>
  );
}

function Step2({
  wizard,
  setWizard,
}: {
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState>>;
}) {
  const options: { value: DsgvoDepth; label: string; hint: string }[] = [
    { value: 'none', label: 'Keine DSGVO-Prüfung', hint: 'DSGVO ist nicht Gegenstand des Assessments.' },
    { value: 'standard', label: 'Standard', hint: 'Grundlegende DSGVO-Anforderungen werden geprüft.' },
    { value: 'deepDive', label: 'Tiefenprüfung', hint: 'Umfassende DSGVO-Analyse inkl. Verarbeitungsverzeichnis und TOMs.' },
  ];
  return (
    <div className={styles.stepContent}>
      <h3 className={styles.stepTitle}>DSGVO-Tiefe</h3>
      <p className={styles.stepHint}>Welchen Umfang soll die datenschutzrechtliche Prüfung haben?</p>
      <div className={styles.radioGroup}>
        {options.map((opt) => (
          <label
            key={opt.value}
            className={`${styles.radioCard} ${wizard.dsgvoDepth === opt.value ? styles.radioCardActive : ''}`}
          >
            <input
              type="radio"
              name="dsgvoDepth"
              value={opt.value}
              checked={wizard.dsgvoDepth === opt.value}
              onChange={() => setWizard((p) => ({ ...p, dsgvoDepth: opt.value }))}
            />
            <div>
              <div className={styles.radioLabel}>{opt.label}</div>
              <div className={styles.radioHint}>{opt.hint}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function Step3({
  wizard,
  setWizard,
}: {
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState>>;
}) {
  const docOptions: { value: DocState; label: string; hint: string }[] = [
    { value: 'complete', label: 'Vollständig', hint: 'Alle relevanten Dokumente sind vorhanden und aktuell.' },
    { value: 'partial', label: 'Teilweise vorhanden', hint: 'Ein Teil der Dokumentation ist vorhanden.' },
    { value: 'missing', label: 'Fehlt weitgehend', hint: 'Kaum Dokumentation vorhanden — hoher Erhebungsaufwand.' },
  ];
  const depthOptions: { value: AssessmentDepth; label: string; hint: string }[] = [
    { value: 'standardCheck', label: 'Standard-Check', hint: 'Strukturierte Überprüfung auf Basis von Standardkriterien.' },
    { value: 'premiumAssessment', label: 'Premium-Assessment', hint: 'Umfassende Analyse mit detaillierter Gap-Bewertung und Maßnahmenplanung.' },
  ];
  return (
    <div className={styles.stepContent}>
      <h3 className={styles.stepTitle}>Dokumentations-Lage & Detailtiefe</h3>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Dokumentations-Lage</label>
        <div className={styles.radioGroup}>
          {docOptions.map((opt) => (
            <label
              key={opt.value}
              className={`${styles.radioCard} ${wizard.docState === opt.value ? styles.radioCardActive : ''}`}
            >
              <input
                type="radio"
                name="docState"
                value={opt.value}
                checked={wizard.docState === opt.value}
                onChange={() => setWizard((p) => ({ ...p, docState: opt.value }))}
              />
              <div>
                <div className={styles.radioLabel}>{opt.label}</div>
                <div className={styles.radioHint}>{opt.hint}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
      <div className={styles.formGroup}>
        <label className={styles.formLabel}>Detailtiefe</label>
        <div className={styles.radioGroup}>
          {depthOptions.map((opt) => (
            <label
              key={opt.value}
              className={`${styles.radioCard} ${wizard.depth === opt.value ? styles.radioCardActive : ''}`}
            >
              <input
                type="radio"
                name="depth"
                value={opt.value}
                checked={wizard.depth === opt.value}
                onChange={() => setWizard((p) => ({ ...p, depth: opt.value }))}
              />
              <div>
                <div className={styles.radioLabel}>{opt.label}</div>
                <div className={styles.radioHint}>{opt.hint}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function Step4({
  wizard,
  setWizard,
}: {
  wizard: WizardState;
  setWizard: React.Dispatch<React.SetStateAction<WizardState>>;
}) {
  return (
    <div className={styles.stepContent}>
      <h3 className={styles.stepTitle}>Add-ons</h3>
      <p className={styles.stepHint}>Optionale Leistungsbausteine, die zum Basisumfang hinzugebucht werden können.</p>
      <div className={styles.addonGroup}>
        <label className={`${styles.addonCard} ${wizard.addOns.executiveTraining ? styles.addonCardActive : ''}`}>
          <input
            type="checkbox"
            checked={wizard.addOns.executiveTraining}
            onChange={(e) =>
              setWizard((p) => ({
                ...p,
                addOns: { ...p.addOns, executiveTraining: e.target.checked },
              }))
            }
          />
          <div>
            <div className={styles.addonLabel}>Executive Training</div>
            <div className={styles.addonHint}>Schulung der Führungsebene zu Compliance-Anforderungen und Haftungsrisiken.</div>
          </div>
        </label>
        <label className={`${styles.addonCard} ${wizard.addOns.auditReadinessCheck ? styles.addonCardActive : ''}`}>
          <input
            type="checkbox"
            checked={wizard.addOns.auditReadinessCheck}
            onChange={(e) =>
              setWizard((p) => ({
                ...p,
                addOns: { ...p.addOns, auditReadinessCheck: e.target.checked },
              }))
            }
          />
          <div>
            <div className={styles.addonLabel}>Audit Readiness Check</div>
            <div className={styles.addonHint}>Vorbereitungscheck auf externe Audits und Zertifizierungsprüfungen.</div>
          </div>
        </label>
      </div>
    </div>
  );
}

// ── Result View ───────────────────────────────────────────────────────────────

function ResultView({
  calc,
  roleRates,
  roles,
  resultTab,
  setResultTab,
  onNewCalc,
  onSetActualPt,
}: {
  calc: CpcCalculationListItem;
  roleRates: CpcRoleRate[];
  roles: CpcRoleDefinition[];
  resultTab: ResultTab;
  setResultTab: (t: ResultTab) => void;
  onNewCalc: () => void;
  onSetActualPt: () => void;
}) {
  const result: AssessmentResult = calc.result;
  const { min = 0, expected = 0, max = 0 } = result.totalPT ?? {};
  const totalRange = max - min || 1;
  const expectedPct = ((expected - min) / totalRange) * 100;
  const estimationRate = result.estimationRate ?? 0;
  const highEstimationRate = estimationRate > 50;

  return (
    <div className={styles.root}>
      {/* Breadcrumb */}
      <div className={styles.resultBreadcrumb}>
        <button className={styles.breadcrumbLink} onClick={onNewCalc}>
          ← Zur Übersicht
        </button>
        <span className={styles.breadcrumbSep}>›</span>
        <span>{calc.revisionLabel}</span>
      </div>

      {/* Bandbreiten-Anzeige */}
      <div className={styles.bandCard}>
        <div className={styles.bandTitle}>Geschätzte Projektzeit (PT)</div>
        <div className={styles.bandValues}>
          <div className={styles.bandItem}>
            <span className={styles.bandItemLabel}>Minimum</span>
            <span className={styles.bandItemValue}>{formatPt(min)} PT</span>
          </div>
          <div className={styles.bandItemExpected}>
            <span className={styles.bandItemLabel}>Erwartet</span>
            <span className={styles.bandExpectedValue}>{formatPt(expected)} PT</span>
          </div>
          <div className={styles.bandItem}>
            <span className={styles.bandItemLabel}>Maximum</span>
            <span className={styles.bandItemValue}>{formatPt(max)} PT</span>
          </div>
        </div>
        <div className={styles.bandBar}>
          <div className={styles.bandBarFill} style={{ left: '0%', width: '100%' }} />
          <div
            className={styles.bandBarMarker}
            style={{ left: `${expectedPct}%` }}
            title={`Erwartet: ${formatPt(expected)} PT`}
          />
        </div>
        <div className={styles.bandBarLabels}>
          <span>{formatPt(min)}</span>
          <span>{formatPt(max)}</span>
        </div>

        {highEstimationRate && (
          <div className={styles.warningBanner}>
            Hinweis: Hohe Schätzunsicherheit ({estimationRate.toFixed(0)} %). Die tatsächlichen Werte können erheblich abweichen.
          </div>
        )}
        <CalculationExplanation result={result} />
      </div>

      {/* Empfehlung */}
      {result.recommendation && (
        <div className={styles.recommendCard}>
          <div className={styles.recommendTitle}>Empfehlung</div>
          <div className={styles.recommendContent}>
            <div>
              <span className={styles.recommendLabel}>Paket:</span>
              <span className={styles.packageBadge}>
                {result.recommendation.package === 'premiumAssessment' ? 'Premium-Assessment' : 'Standard-Check'}
              </span>
            </div>
            {result.recommendation.addOns.length > 0 && (
              <div>
                <span className={styles.recommendLabel}>Empfohlene Add-ons:</span>
                {result.recommendation.addOns.join(', ')}
              </div>
            )}
            {result.recommendation.warnings.length > 0 && (
              <ul className={styles.warningList}>
                {result.recommendation.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Sub-Tabs */}
      <div className={styles.resultTabBar}>
        {(['phases', 'roles', 'factors', 'assumptions'] as ResultTab[]).map((t) => {
          const labels: Record<ResultTab, string> = {
            phases: 'Phasen',
            roles: 'Rollen',
            factors: 'Faktoren',
            assumptions: 'Annahmen',
          };
          return (
            <button
              key={t}
              className={`${styles.resultTab} ${resultTab === t ? styles.resultTabActive : ''}`}
              onClick={() => setResultTab(t)}
            >
              {labels[t]}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className={styles.resultTabContent}>
        {resultTab === 'phases' && <PhasesTab result={result} />}
        {resultTab === 'roles' && <RolesTab result={result} roleRates={roleRates} roles={roles} />}
        {resultTab === 'factors' && <FactorsTab result={result} />}
        {resultTab === 'assumptions' && <AssumptionsTab result={result} />}
      </div>

      {/* Footer */}
      <div className={styles.resultFooter}>
        <span className={styles.factorVersion}>Faktor-Set-Version: {calc.factorSet.version}</span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className={styles.secondaryBtn} onClick={onNewCalc}>
            Neue Kalkulation
          </button>
          {!calc.actualPt && (
            <button className={styles.primaryBtn} onClick={onSetActualPt}>
              Ist-PT eintragen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Calculation Explanation ───────────────────────────────────────────────────

const SIZE_LABELS: Record<string, string> = { S: 'Klein (S)', M: 'Mittel (M)', L: 'Groß (L)' };

function CalcStepRow({
  num, label, desc, pt, isDiscount, isNeutral, isFinal,
}: {
  num: number; label: string; desc: string; pt: number | undefined;
  isDiscount?: boolean; isNeutral?: boolean; isFinal?: boolean;
}) {
  const safePt = pt ?? 0;
  return (
    <div className={`${styles.calcStepRow} ${isFinal ? styles.calcStepFinal : ''}`}>
      <div className={styles.calcStepLeft}>
        <span className={`${styles.calcStepNum} ${isFinal ? styles.calcStepNumFinal : ''}`}>{num}</span>
        <div>
          <div className={styles.calcStepLabel}>{label}</div>
          <div className={`${styles.calcStepDesc} ${isDiscount ? styles.calcStepDiscount : ''} ${isNeutral ? styles.calcStepNeutral : ''}`}>
            {desc}
          </div>
        </div>
      </div>
      <div className={`${styles.calcStepPt} ${isFinal ? styles.calcStepPtFinal : ''}`}>
        {(Math.round(safePt * 10) / 10).toFixed(1)} PT
      </div>
    </div>
  );
}

function CalculationExplanation({ result }: { result: AssessmentResult }) {
  const steps = result.calculationSteps;
  if (!steps) return null;

  const af = result.appliedFactors;
  const n = result.frameworkScopeApplied.length;

  return (
    <details className={styles.calcExplanation}>
      <summary className={styles.calcExplanationToggle}>
        Berechnung nachvollziehen
      </summary>
      <div className={styles.calcSteps}>
        <CalcStepRow
          num={1}
          label="Basis-PT"
          desc={`Größenklasse ${SIZE_LABELS[af.sizeClass] ?? af.sizeClass} — Summe aller Projekt-Phasen`}
          pt={steps.ptBasis}
        />
        <CalcStepRow
          num={2}
          label="Framework-Scope"
          desc={`${n} Framework${n !== 1 ? 's' : ''} mit Overlap-Multiplikator ×${(steps.overlapMultiplier ?? 1).toFixed(2)}`}
          pt={steps.ptScope}
        />
        <CalcStepRow
          num={3}
          label="Branche & Standort"
          desc={`Branchenfaktor ×${(af.industrySectorFactor ?? 1).toFixed(2)} · Standortfaktor ×${(af.locationFactor ?? 1).toFixed(2)}`}
          pt={steps.ptSector}
        />
        <CalcStepRow
          num={4}
          label="Strukturaufschläge"
          desc={
            af.criticalFunctionsSurcharge > 0 || af.thirdPartySurcharge > 0
              ? `Krit. Funktionen +${((af.criticalFunctionsSurcharge ?? 0) * 100).toFixed(0)}% · Drittparteien +${((af.thirdPartySurcharge ?? 0) * 100).toFixed(0)}%`
              : 'Keine Aufschläge'
          }
          pt={steps.ptStruct}
          isNeutral={!af.criticalFunctionsSurcharge && !af.thirdPartySurcharge}
        />
        <CalcStepRow
          num={5}
          label="Reifegradrabatt"
          desc={(af.maturityDiscount ?? 0) > 0
            ? `ISMS/BCMS-Rabatt −${((af.maturityDiscount ?? 0) * 100).toFixed(0)}%`
            : 'Kein Rabatt (keine Zertifizierung)'}
          pt={steps.ptMaturity}
          isDiscount={(af.maturityDiscount ?? 0) > 0}
          isNeutral={!(af.maturityDiscount ?? 0)}
        />
        <CalcStepRow
          num={6}
          label="Dokumentation, Tiefe & DSGVO"
          desc={`Doku ×${(af.docFactor ?? 1).toFixed(2)} · Tiefe ×${(af.depthFactor ?? 1).toFixed(2)} · DSGVO ×${(af.dsgvoFactor ?? 1).toFixed(2)}`}
          pt={steps.ptDoc}
        />
        <CalcStepRow
          num={7}
          label="Annahme-Risiko"
          desc={`Schätzanteil ${((result.estimationRate ?? 0) * 100).toFixed(0)}% → Faktor ×${(af.assumptionRiskFactor ?? 1).toFixed(2)}`}
          pt={result.totalPT?.expected ?? 0}
          isFinal
        />
      </div>
    </details>
  );
}

// ── Result Sub-Tabs ───────────────────────────────────────────────────────────

function PhasesTab({ result }: { result: AssessmentResult }) {
  const total = result.phaseBreakdown.reduce((s, p) => s + p.expectedPT, 0);
  return (
    <div className={styles.dataTable}>
      <table>
        <thead>
          <tr>
            <th>Phase</th>
            <th style={{ textAlign: 'right' }}>PT (erwartet)</th>
            <th style={{ textAlign: 'right' }}>Anteil</th>
          </tr>
        </thead>
        <tbody>
          {result.phaseBreakdown.map((p) => (
            <tr key={p.phase}>
              <td>{PHASE_LABELS[p.phase] ?? p.phase}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatPt(p.expectedPT)}</td>
              <td style={{ textAlign: 'right', color: 'var(--color-text-muted)' }}>
                {total > 0 ? ((p.expectedPT / total) * 100).toFixed(0) : 0} %
              </td>
            </tr>
          ))}
          {result.addOnBreakdown.length > 0 && result.addOnBreakdown.map((a) => (
            <tr key={a.name} className={styles.addOnRow}>
              <td>Add-on: {a.name}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatPt(a.pt)}</td>
              <td style={{ textAlign: 'right', color: 'var(--color-text-muted)' }}>—</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td style={{ fontWeight: 700 }}>Gesamt</td>
            <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatPt(result.totalPT?.expected)}</td>
            <td style={{ textAlign: 'right', fontWeight: 700 }}>100 %</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function RolesTab({
  result,
  roleRates,
  roles,
}: {
  result: AssessmentResult;
  roleRates: CpcRoleRate[];
  roles: CpcRoleDefinition[];
}) {
  const rb = result.roleBreakdown;
  const roleKeys = Object.keys(rb);
  const rateMap = Object.fromEntries(roleRates.map((r) => [r.role, parseFloat(r.dailyRateEur)]));
  const hasRates = roleRates.length > 0;

  return (
    <div className={styles.dataTable}>
      <table>
        <thead>
          <tr>
            <th>Rolle</th>
            <th style={{ textAlign: 'right' }}>PT</th>
            {hasRates && <th style={{ textAlign: 'right' }}>Tagessatz</th>}
            {hasRates && <th style={{ textAlign: 'right' }}>Betrag (EUR)</th>}
          </tr>
        </thead>
        <tbody>
          {roleKeys.map((key) => {
            const pt = rb[key] ?? 0;
            const rate = rateMap[key];
            return (
              <tr key={key}>
                <td>{getRoleLabel(key, roles)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatPt(pt)}</td>
                {hasRates && (
                  <td style={{ textAlign: 'right', color: 'var(--color-text-muted)' }}>
                    {rate != null ? `${rate.toLocaleString('de-DE')} €` : '—'}
                  </td>
                )}
                {hasRates && (
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>
                    {rate != null && pt > 0
                      ? (pt * rate).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })
                      : '—'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        {hasRates && (
          <tfoot>
            <tr>
              <td style={{ fontWeight: 700 }}>Gesamt</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatPt(result.totalPT?.expected)}</td>
              <td />
              <td style={{ textAlign: 'right', fontWeight: 700 }}>
                {roleKeys
                  .reduce((sum, key) => {
                    const pt = rb[key] ?? 0;
                    const rate = rateMap[key];
                    return rate != null ? sum + pt * rate : sum;
                  }, 0)
                  .toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function FactorsTab({ result }: { result: AssessmentResult }) {
  const af = result.appliedFactors;
  const entries = Object.entries(af) as [keyof typeof af, string | number][];
  return (
    <div className={styles.dataTable}>
      <table>
        <thead>
          <tr>
            <th>Faktor</th>
            <th style={{ textAlign: 'right' }}>Wert</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([key, val]) => (
            <tr key={key}>
              <td>{FACTOR_LABELS[key] ?? key}</td>
              <td style={{ textAlign: 'right', fontWeight: 600 }}>
                {typeof val === 'number' ? val.toFixed(val % 1 === 0 ? 0 : 2) : String(val)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AssumptionsTab({ result }: { result: AssessmentResult }) {
  return (
    <div className={styles.assumptionsPanel}>
      <div className={styles.assumptionRow}>
        <span className={styles.assumptionLabel}>Schätzunsicherheit (Estimation Rate)</span>
        <span
          className={`${styles.estimationRateBadge} ${(result.estimationRate ?? 0) > 50 ? styles.estimationRateHigh : ''}`}
        >
          {(result.estimationRate ?? 0).toFixed(1)} %
        </span>
      </div>
      <div className={styles.assumptionRow}>
        <span className={styles.assumptionLabel}>Angewandte Frameworks</span>
        <span>
          {result.frameworkScopeApplied
            .map((fw) => FRAMEWORK_LABELS[fw] ?? fw)
            .join(', ')}
        </span>
      </div>
      <div className={styles.assumptionRow}>
        <span className={styles.assumptionLabel}>Empfohlenes Paket</span>
        <span>
          {result.recommendation.package === 'premiumAssessment'
            ? 'Premium-Assessment'
            : 'Standard-Check'}
        </span>
      </div>
      {result.recommendation.preAssessmentRecommended && (
        <div className={styles.preAssessmentHint}>
          Ein Pre-Assessment wird empfohlen, um die Datenbasis vor dem Haupt-Assessment zu verifizieren.
        </div>
      )}
      {result.recommendation.warnings.length > 0 && (
        <div className={styles.assumptionWarnings}>
          <div className={styles.assumptionWarningsTitle}>Hinweise &amp; Warnungen</div>
          <ul>
            {result.recommendation.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Actual PT Form ────────────────────────────────────────────────────────────

function ActualPtForm({
  form,
  setForm,
  roles,
  onSubmit,
  onCancel,
  isPending,
}: {
  form: Record<string, string>;
  setForm: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  roles: { key: string; label: string }[];
  onSubmit: () => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  return (
    <div>
      <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
        Trage die tatsächlich geleisteten Projekttage ein, um Soll-Ist-Vergleiche zu ermöglichen.
      </p>
      <div className={styles.actualPtGrid}>
        <label className={styles.formLabel}>Gesamt-PT (Ist)</label>
        <input
          className={styles.numberInput}
          type="number"
          min={0}
          step={0.5}
          placeholder="0"
          value={form.totalPT ?? ''}
          onChange={(e) => setForm((p) => ({ ...p, totalPT: e.target.value }))}
        />
        {roles.map((role) => (
          <React.Fragment key={role.key}>
            <label className={styles.formLabel}>{role.label}</label>
            <input
              className={styles.numberInput}
              type="number"
              min={0}
              step={0.5}
              placeholder="0"
              value={form[role.key] ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, [role.key]: e.target.value }))}
            />
          </React.Fragment>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
        <button className={styles.secondaryBtn} onClick={onCancel} disabled={isPending}>
          Abbrechen
        </button>
        <button className={styles.primaryBtn} onClick={onSubmit} disabled={isPending || !form.totalPT}>
          {isPending ? 'Speichern...' : 'Ist-PT speichern'}
        </button>
      </div>
    </div>
  );
}
